import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  PlusIcon,
  TrashIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { toast } from 'react-hot-toast';

interface AvailableScreen {
  id: string;
  screen_code: string;
  display_name: string;
  description?: string;
  icon?: string;
  route_path: string;
  translation_key?: string;
  module_name: string;
  module_code: string;
  module_category: string;
  has_read_permission: boolean;
}

interface UserNavigationPreference {
  id: string;
  screen_id: string;
  preference_type: 'TOP_NAVBAR' | 'QUICK_ACTION';
  display_order: number;
  custom_label?: string | null;
  custom_icon?: string | null;
  is_active: boolean;
  screen_display_name?: string;
  screen_route_path?: string;
  screen_icon?: string;
  screen_translation_key?: string;
}

interface UserNavigationPreferencesResponse {
  top_navbar: UserNavigationPreference[];
  quick_actions: UserNavigationPreference[];
}

interface SortableItemProps {
  id: string;
  preference: UserNavigationPreference;
  availableScreens: AvailableScreen[];
  onRemove: (id: string) => void;
  type: 'TOP_NAVBAR' | 'QUICK_ACTION';
}

const SortableItem: React.FC<SortableItemProps> = ({ id, preference, availableScreens, onRemove }) => {
  const { t } = useLanguage();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const screen = availableScreens.find(s => s.id === preference.screen_id);
  const displayName = (() => {
    if (preference.custom_label?.trim()) return preference.custom_label;
    const tk = preference.screen_translation_key;
    if (tk?.trim()) {
      const tr = t(tk);
      if (tr !== tk) return tr;
    }
    return preference.screen_display_name || screen?.display_name || t('navigation.unknownScreen');
  })();

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)]"
      >
        <Bars3Icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <div className="font-medium text-[var(--color-surface-text)]">{displayName}</div>
        {screen && (
          <div className="text-sm text-[var(--color-secondary-text)]">
            {screen.module_name} • {screen.route_path}
          </div>
        )}
      </div>
      <button
        onClick={() => onRemove(id)}
        className="p-1 text-[var(--color-error)] hover:bg-[var(--color-error-bg)] rounded"
        title={t('common.remove')}
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

interface PreferenceSectionProps {
  title: string;
  type: 'TOP_NAVBAR' | 'QUICK_ACTION';
  preferences: UserNavigationPreference[];
  availableScreens: AvailableScreen[];
  onAdd: (screenId: string, type: 'TOP_NAVBAR' | 'QUICK_ACTION') => void;
  onRemove: (id: string) => void;
  onReorder: (type: 'TOP_NAVBAR' | 'QUICK_ACTION', preferenceIds: string[]) => void;
}

const PreferenceSection: React.FC<PreferenceSectionProps> = ({
  title,
  type,
  preferences,
  availableScreens,
  onAdd,
  onRemove,
  onReorder
}) => {
  const { t } = useLanguage();
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter available screens that aren't already added
  const addedScreenIds = new Set(preferences.map(p => p.screen_id));
  const availableToAdd = availableScreens.filter(
    screen => !addedScreenIds.has(screen.id) &&
    screen.has_read_permission &&
    screen.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = preferences.findIndex(p => p.id === active.id);
    const newIndex = preferences.findIndex(p => p.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newPreferences = [...preferences];
      const [removed] = newPreferences.splice(oldIndex, 1);
      newPreferences.splice(newIndex, 0, removed);
      const preferenceIds = newPreferences.map(p => p.id);
      onReorder(type, preferenceIds);
    }
  };

  const handleAdd = (screenId: string) => {
    onAdd(screenId, type);
    setShowAddMenu(false);
    setSearchTerm('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">{title}</h3>
        <button
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-md hover:opacity-90"
        >
          <PlusIcon className="w-4 h-4" />
          {t('common.add')}
        </button>
      </div>

      {showAddMenu && (
        <div className="p-4 bg-[var(--color-secondary-bg)] border border-[var(--color-border)] rounded-md">
          <div className="mb-3">
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)]"
            />
          </div>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {availableToAdd.length === 0 ? (
              <div className="text-center py-4 text-[var(--color-secondary-text)]">
                {t('navigation.noItemsAvailable')}
              </div>
            ) : (
              availableToAdd.map(screen => (
                <button
                  key={screen.id}
                  onClick={() => handleAdd(screen.id)}
                  className="w-full text-left p-3 bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-secondary-bg)] transition-colors"
                >
                  <div className="font-medium text-[var(--color-surface-text)]">{screen.display_name}</div>
                  <div className="text-sm text-[var(--color-secondary-text)]">
                    {screen.module_name} • {screen.route_path}
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => {
              setShowAddMenu(false);
              setSearchTerm('');
            }}
            className="mt-3 w-full px-3 py-2 border border-[var(--color-border)] rounded-md text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-2">
          {preferences.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-secondary-text)] border border-dashed border-[var(--color-border)] rounded-md">
              {t('navigation.noItemsConfigured')}
            </div>
          ) : (
            preferences.map((preference) => (
              <SortableItem
                key={preference.id}
                id={preference.id}
                preference={preference}
                availableScreens={availableScreens}
                onRemove={onRemove}
                type={type}
              />
            ))
          )}
        </div>
      </DndContext>
    </div>
  );
};

export const UserNavigationConfig: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<UserNavigationPreferencesResponse>({
    top_navbar: [],
    quick_actions: []
  });
  const [availableScreens, setAvailableScreens] = useState<AvailableScreen[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prefsResponse, screensResponse] = await Promise.all([
        api.get('/navigation/user/preferences'),
        api.get('/navigation/user/available-screens')
      ]);

      setPreferences(prefsResponse.data.data);
      setAvailableScreens(screensResponse.data.data);
    } catch (error: any) {
      console.error('Error loading navigation configuration:', error);
      toast.error(error.response?.data?.message || t('navigation.errorLoadingConfig'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (screenId: string, type: 'TOP_NAVBAR' | 'QUICK_ACTION') => {
    try {
      await api.post('/navigation/user/preferences', {
        screen_id: screenId,
        preference_type: type
      });

      // Reload preferences to get updated data
      await loadData();
      toast.success(t('navigation.itemAdded'));
    } catch (error: any) {
      console.error('Error adding navigation item:', error);
      toast.error(error.response?.data?.message || t('navigation.errorAddingItem'));
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await api.delete(`/navigation/user/preferences/${id}`);
      await loadData();
      toast.success(t('navigation.itemRemoved'));
    } catch (error: any) {
      console.error('Error removing navigation item:', error);
      toast.error(error.response?.data?.message || t('navigation.errorRemovingItem'));
    }
  };

  const handleReorder = async (type: 'TOP_NAVBAR' | 'QUICK_ACTION', preferenceIds: string[]) => {
    try {
      await api.post('/navigation/user/preferences/reorder', {
        preference_type: type,
        preference_ids: preferenceIds
      });
      // Update local state optimistically
      if (type === 'TOP_NAVBAR') {
        const reordered = preferenceIds
          .map(id => preferences.top_navbar.find(p => p.id === id))
          .filter((p): p is UserNavigationPreference => p !== undefined);
        setPreferences({ ...preferences, top_navbar: reordered });
      } else {
        const reordered = preferenceIds
          .map(id => preferences.quick_actions.find(p => p.id === id))
          .filter((p): p is UserNavigationPreference => p !== undefined);
        setPreferences({ ...preferences, quick_actions: reordered });
      }
      toast.success(t('navigation.itemsReordered'));
    } catch (error: any) {
      console.error('Error reordering navigation items:', error);
      toast.error(error.response?.data?.message || t('navigation.errorReordering'));
      // Reload on error to restore correct order
      await loadData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--color-secondary-text)]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <p className="text-[var(--color-secondary-text)]">
          {t('navigation.configDescription')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <PreferenceSection
            title={t('navigation.topNavbar')}
            type="TOP_NAVBAR"
            preferences={preferences.top_navbar}
            availableScreens={availableScreens}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onReorder={handleReorder}
          />
        </div>

        <div className="space-y-4">
          <PreferenceSection
            title={t('navigation.quickActions')}
            type="QUICK_ACTION"
            preferences={preferences.quick_actions}
            availableScreens={availableScreens}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onReorder={handleReorder}
          />
        </div>
      </div>
    </div>
  );
};

